import { useStoreLogger } from '../logger.ts'
import type {
  IEventHostResponse,
  IEventHostRequest,
  IEventRequestWithBeeNumber,
  IEventResponse,
  IEventCreateRequest,
  IEventCreateResponse,
  IEvent,
  IEventListResponse,
  IEventTypeListResponse,
  IEventGuestCreateRequest,
  IEventGuestCreateResponse,
  IEventRequest,
  IEventGuestListResponse,
  IEventGuestDeleteRequest,
  IEventUpdateRequest,
  IShipNowSubmitRequest,
  IShipNowCartSubmitResponse,
  IMasterOrderSubmitRequest,
  IMasterOrderSubmitResponse,
  IEventAddressRequest,
  IEventGuestUpdateRequest,
  IEventGuestSendInvitationRequest,
  IEventGuestMakeHostRequest,
  IHostPortalGetListResponse,
  IHostPortalDetailGetRequest,
  IHostPortalDetailGetResponse,
  IEventGuest,
  IEventSubmittedOrder,
} from '../types/EventTypes'
import type { IOrderDetailGetItem } from '../types/OrderTypes'
import { GetOrderDetail } from './OrderService.ts'
import { CreateEvent, GetEvent, GetEventHostList, GetEventList, GetEventTypeList,
          CreateEventGuest, AddressCreate, GetEventGuests, GetEventOrders, UpdateEvent, DeleteEvent, CancelEvent, CloseEvent, GetEventMasterOrder, ShipNowCartSubmit, DeleteEventGuest,
          MasterOrderSubmit, EventAddressCreate, UpdateEventGuest,
          SendInvitationEventGuest, MakeHostEventGuest, HostPortalGetList, HostPortalDetailGet} from '../api/Event.ts'
import type { IAddressCreate, IAddressVerification } from '../types/CountryCultureTypes'

  
export async function GetEventHosts(
  request: IEventHostRequest,
): Promise<IEventHostResponse> {
  const logger = useStoreLogger()
  logger.debug('GetEventHosts', request)
  const eventHostList = await GetEventHostList(request)
  return eventHostList
}

export async function EventGet(
  request: IEventRequestWithBeeNumber,
): Promise<IEventResponse> {
  const logger = useStoreLogger()
  logger.debug('GetEvent', request)
  const event = await GetEvent(request)
  return event
}

export async function EventCreate(
  request: IEventCreateRequest,
): Promise<IEventCreateResponse> {
  const logger = useStoreLogger()
  logger.debug('EventCreate', request)
  const event = await CreateEvent(request)
  return event
}

export async function EventGuestCreate(
  request: IEventGuestCreateRequest,
): Promise<IEventGuestCreateResponse> {
  const logger = useStoreLogger()
  logger.debug('EventGuestCreate', request)
  const event = await CreateEventGuest(request)
  return event
}

export async function EventGuestDelete(request: IEventGuestDeleteRequest)
{
  const logger = useStoreLogger()
  logger.debug('EventGuestDelete', request)
  const event = await DeleteEventGuest(request)
  return event
}

export async function EventGuestMakeHost(request: IEventGuestMakeHostRequest)
{
  const logger = useStoreLogger()
  logger.debug('EventGuestMakeHost', request)
  const event = await MakeHostEventGuest(request)
  return event
}

export async function EventGuestSendInvitation(request: IEventGuestSendInvitationRequest)
{
  const logger = useStoreLogger()
  logger.debug('EventGuestSendInvitation', request)
  const event = await SendInvitationEventGuest(request)
  return event
}

export async function EventGuestUpdate(request: IEventGuestUpdateRequest)
{
  const logger = useStoreLogger()
  logger.debug('EventGuestUpdate', request)
  const event = await UpdateEventGuest(request)
  return event
}

export const EventLocalStorageKey = 'AoeEvent_'

export function GetEventFromLocalStorage(
  request: IEventRequestWithBeeNumber
): IEvent | null {
  const logger = useStoreLogger()
  logger.debug('GetEventFromLocalStorage', request.eventIdToken)
  const key = EventLocalStorageKey + request.eventIdToken
  const json = localStorage.getItem(key)
  if (json) {
    logger.debug('Event found in local storage')

    const parsedEvent = JSON.parse(json)
    const ev: IEvent = parsedEvent

    ev.eventCity = parsedEvent.address.city
    ev.eventState = parsedEvent.address.stateCode

    return ev
  }
  logger.debug('Event not found in local storage')
  return null
}

export async function GetEventFromLocalStorageOrApi(
  request: IEventRequestWithBeeNumber
): Promise<IEvent | null> {
  const logger = useStoreLogger()
  logger.debug('GetEventFromLocalStorageOrApi', request.eventIdToken)

  let ev = GetEventFromLocalStorage(request)
  if (ev) return ev

  const evResponse = await EventGet(request)
  if (evResponse) {
    SaveEventToLocalStorage(evResponse)
    ev = GetEventFromLocalStorage(request)
  }
  return ev
}

export function SaveEventToLocalStorage(eventResponse: IEventResponse) {
  const newValue = JSON.stringify(eventResponse.item)
  const logger = useStoreLogger()
  logger.debug('SaveEventToLocalStorage', newValue)
  const key = EventLocalStorageKey + eventResponse.item.eventIdToken
  localStorage.setItem(key, newValue)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

export async function EventOpenGetList(): Promise<IEventListResponse> {
  const logger = useStoreLogger()
  logger.debug('EventGetList')
  const event = await GetEventList({
    DatabaseFilter: {
      Where: {
        OperatorType: "Or",
        Operands: [
          {
            Field: "CddCodeGuidEventStatus",
            Value: '8823444282138328140353289620', // Open status
            FilterType: "Equal",
          },
          {
            Field: "CddCodeGuidEventStatus",
            Value: '16577494489146403407298725011', // In-Progress status
            FilterType: "Equal",
          },
        ],
      },
    },
  })
  return event
}

export async function EventClosedGetList(): Promise<IEventListResponse> {
  const logger = useStoreLogger()
  logger.debug('EventGetList')
  const event = await GetEventList({
    DatabaseFilter: {
      Where: {
        OperatorType: "Or",
        Operands: [
          {
            Field: "CddCodeGuidEventStatus",
            Value: '18758396667731244062683925647',
            FilterType: "Equal",
          },
          {
            // Cancelled events stay visible (read-only). "EventStatus" is rewritten
            // to CddCodeGuidEventStatus with the tenant's Cancelled guid server-side,
            // so the code letter can be used instead of a hardcoded guid.
            Field: "EventStatus",
            Value: 'X',
            FilterType: "Equal",
          },
        ],
      },
    },
  })
  return event
}

export async function EventTypesGetList(): Promise<IEventTypeListResponse> {
  const logger = useStoreLogger()
  logger.debug('EventTypesGetList')
  const eventTypes = await GetEventTypeList()
  return eventTypes
}

export async function AddressAdd(addressCreate: IAddressCreate) : Promise<IAddressVerification> {
  const addressVerification: IAddressVerification = { addressValidationMessageId: null, addressId: null, items: [], item: null }
  await AddressCreate(addressCreate).then(response => {
    if (response.addressValidationMessageId != undefined && response.addressValidationMessageId.length > 0) {

      addressVerification.addressValidationMessageId = response.addressValidationMessageId;

      if (response.validAddresses != undefined && response.validAddresses.length > 0) {
        for(const t of response.validAddresses)
        {
          addressVerification.items.push(t)
        }

        let count = 1;
        for (const a of addressVerification.items) {
          a.addressId = count
          count++
        }
      }

      return addressVerification;
    }
    else
    {
      addressVerification.item = response.item
      addressVerification.addressId = response.addressId
    }
  })

  return addressVerification;
}

export async function EventGuestGetList(
  request: IEventRequest,
): Promise<IEventGuestListResponse> {
  const logger = useStoreLogger()
  logger.debug('EventGuestGetList', request)
  const guests = await GetEventGuests(request)
  return guests
}

/**
 * Submitted orders linked to an event.
 *
 * The source of truth is EventOrderGetList - every order tagged with the event, the
 * same set the admin event summary shows. The guest list (EventGuestGetList) only
 * knows about an order once the guest's event contact has been stamped with its
 * orderId, and that stamp can be missing (e.g. a Ship Now order whose guest's
 * address book entry had been re-pointed to another customer), which used to drop
 * the order from the party order grid and its totals entirely.
 *
 * The guest list is still used for the guest's name as the consultant knows it, and
 * any guest order not returned by EventOrderGetList (e.g. while the backend endpoint
 * isn't deployed yet) is kept so nothing regresses. The money breakdown comes from a
 * per-order OrderDetailGet; those run in parallel and a failure on one order degrades
 * that row to the known total rather than dropping it or failing the whole list.
 */
export async function EventSubmittedOrderGetList(
  request: IEventRequest,
): Promise<IEventSubmittedOrder[]> {
  const logger = useStoreLogger()
  logger.debug('EventSubmittedOrderGetList', request)

  const [guests, eventOrders] = await Promise.all([
    GetEventGuests(request),
    GetEventOrders(request).catch(error => {
      logger.error('EventSubmittedOrderGetList EventOrderGetList failed - falling back to guest list orders', error)
      return undefined
    }),
  ])

  // The guest list serializes orderId as a string (SfaEventContactViewModel.OrderId is a
  // string column) despite IEventGuest typing it as a number, so normalize it - otherwise
  // the Map lookups below never match an event order and the same order is listed twice.
  const guestsByOrderId = new Map<number, IEventGuest>()
  for (const guest of guests?.items ?? []) {
    const orderId = Number(guest.orderId)
    if (Number.isFinite(orderId) && orderId > 0 && !!guest.aoeOrderIdToken) {
      guestsByOrderId.set(orderId, guest)
    }
  }

  const orderRefs = new Map<number, ISubmittedOrderRef>()
  for (const order of eventOrders?.items ?? []) {
    const orderId = Number(order.aoeOrderId)
    if (Number.isFinite(orderId) && orderId > 0 && !!order.aoeOrderIdToken && !orderRefs.has(orderId)) {
      orderRefs.set(orderId, {
        aoeOrderId: orderId,
        aoeOrderIdToken: order.aoeOrderIdToken,
        knownTotal: order.total,
        guest: guestsByOrderId.get(orderId),
      })
    }
  }
  for (const [orderId, guest] of guestsByOrderId) {
    if (!orderRefs.has(orderId)) {
      orderRefs.set(orderId, {
        aoeOrderId: orderId,
        aoeOrderIdToken: guest.aoeOrderIdToken!,
        knownTotal: guest.orderTotal,
        guest: guest,
      })
    }
  }

  const submittedOrders = await Promise.all(
    [...orderRefs.values()].map(async ref => {
      try {
        const detail = await GetOrderDetail(ref.aoeOrderIdToken)
        // The consultant's own order on the event isn't a guest order - the guest list never
        // returned it, so keep it out here too unless it belongs to an event contact (a
        // consultant who is also the host).
        if (!ref.guest && IsConsultantOwnOrder(detail?.item)) return null
        return BuildSubmittedOrder(ref, detail?.item)
      } catch (error) {
        logger.error('EventSubmittedOrderGetList order detail failed', ref.aoeOrderIdToken, error)
        return BuildSubmittedOrder(ref, undefined)
      }
    }),
  )

  return submittedOrders.filter((order): order is IEventSubmittedOrder => order != null)
}

function IsConsultantOwnOrder(order: IOrderDetailGetItem | undefined): boolean {
  return order?.beeNumberCustomer != null
    && order?.beeNumberConsultant != null
    && order.beeNumberCustomer === order.beeNumberConsultant
}

interface ISubmittedOrderRef {
  aoeOrderId: number
  aoeOrderIdToken: string
  knownTotal: number | null
  guest: IEventGuest | undefined
}

function BuildSubmittedOrder(
  ref: ISubmittedOrderRef,
  order: IOrderDetailGetItem | undefined,
): IEventSubmittedOrder {
  const skus = order?.skus ?? []

  // Mirrors CartTotalsService so a submitted row and a cart row are derived the
  // same way: subtotal before discounts, discounts summed off the line items.
  const productSubtotal = skus.reduce((total, sku) => total + sku.price * sku.quantity, 0)
  const totalDiscountAmount = skus.reduce((total, sku) => total + (sku.totalDiscountAmount ?? 0), 0)

  // Every charge counts, including refunded ones - the charge was still applied
  // to the order, and excluding it would show a balance due on a paid order.
  const paymentTotal = (order?.payments ?? []).reduce((total, payment) => total + payment.charge, 0)

  const total = order?.total ?? ref.knownTotal ?? 0

  return {
    aoeOrderId: order?.aoeOrderId ?? ref.aoeOrderId,
    aoeOrderIdToken: ref.aoeOrderIdToken,
    // Prefer the guest's name from the event guest list; an order with no matching
    // guest falls back to the customer on the order itself.
    customerFirstName: ref.guest?.firstName ?? order?.customer?.firstName ?? '',
    customerLastName: ref.guest?.lastName ?? order?.customer?.lastName ?? '',
    productSubtotal: productSubtotal,
    totalDiscountAmount: totalDiscountAmount,
    shippingCost: order?.shippingCost ?? 0,
    total: total,
    paymentTotal: paymentTotal,
    balance: total - paymentTotal,
  }
}

export async function EventUpdate(
  request: IEventUpdateRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventUpdate', request)
  const event = await UpdateEvent(request)

  const key = EventLocalStorageKey + request.eventIdToken
  localStorage.removeItem(key)

  return event
}

export async function EventDelete(
  request: IEventRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventDelete', request)
  const event = await DeleteEvent(request)

  const key = EventLocalStorageKey + request.eventIdToken
  localStorage.removeItem(key)

  return event
}

export async function EventCancel(
  request: IEventRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventCancel', request)
  const event = await CancelEvent(request)

  const key = EventLocalStorageKey + request.eventIdToken
  localStorage.removeItem(key)

  return event
}

export async function EventClose(
  request: IEventRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventClose', request)
  const event = await CloseEvent(request)

  const key = EventLocalStorageKey + request.eventIdToken
  localStorage.removeItem(key)

  return event
}

export async function EventMasterOrderGet(
  request: IEventRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventMasterOrderGet', request)
  const eventMO = await GetEventMasterOrder(request)

  return eventMO
}

export async function EventShipNowCartSubmit(
  request: IShipNowSubmitRequest,
) : Promise<IShipNowCartSubmitResponse>  {
  const logger = useStoreLogger()
  logger.debug('EventShipNowCartSubmit', request)
  const eventMO = await ShipNowCartSubmit(request)

  return eventMO
}

export async function EventMasterOrderSubmit(
  request: IMasterOrderSubmitRequest,
) : Promise<IMasterOrderSubmitResponse>  {
  const logger = useStoreLogger()
  logger.debug('EventMasterOrderSubmit', request)
  const eventMO = await MasterOrderSubmit(request)

  return eventMO
}

export async function CreateEventAddress(
  request: IEventAddressRequest,
) {
  const logger = useStoreLogger()
  logger.debug('EventMasterOrderSubmit', request)
  const eventMO = await EventAddressCreate(request)

  return eventMO
}

export async function GetHostPortalThemes(): Promise<IHostPortalGetListResponse> {
  const logger = useStoreLogger()
  logger.debug('HostPortalGetList')
  const response = await HostPortalGetList()

  return response
}

export async function GetHostPortalDetail(themeName: string, messageType: number, cultureName: string): Promise<IHostPortalDetailGetResponse> {
  const logger = useStoreLogger()
  logger.debug('HostPortalDetailGet', { themeName, messageType, cultureName })
  const request: IHostPortalDetailGetRequest = {
    themeName,
    messageType,
    cultureName
  }
  const response = await HostPortalDetailGet(request)

  return response
}
